import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMassMigrationWizardActions } from '@/stores/massMigrationWizardStore';

interface WizardStep {
  id: number;
  title: string;
}

const MASS_WIZARD_STEPS: WizardStep[] = [
  { id: 0, title: 'Org Type' },
  { id: 1, title: 'Upload CSV' },
  { id: 2, title: 'ID Validation' },
  { id: 3, title: 'Module Selection' },
  { id: 4, title: 'Configuration' },
  { id: 5, title: 'Pre-flight' },
  { id: 6, title: 'Confirm' },
];

interface WizardProgressProps {
  currentStep: number;
}

export function WizardProgress({ currentStep }: WizardProgressProps) {
  const { setStep } = useMassMigrationWizardActions();

  return (
    <nav aria-label="Mass migration wizard progress" className="mb-8">
      <ol className="flex items-center justify-between">
        {MASS_WIZARD_STEPS.map((wizardStep, index) => {
          const isCompleted = wizardStep.id < currentStep;
          const isCurrent = wizardStep.id === currentStep;
          const isPast = wizardStep.id < currentStep;
          const isLast = index === MASS_WIZARD_STEPS.length - 1;
          const isClickable = isPast;

          return (
            <li key={wizardStep.id} className={cn('flex items-start', !isLast && 'flex-1')}>
              <div className="flex flex-col items-center">
                <button
                  type="button"
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full border-2 transition-colors',
                    isCompleted
                      ? 'border-primary bg-primary text-primary-foreground'
                      : isCurrent
                        ? 'border-primary bg-background text-primary'
                        : 'border-muted bg-background text-muted-foreground',
                    isClickable
                      ? 'cursor-pointer hover:opacity-80'
                      : 'cursor-default',
                  )}
                  aria-current={isCurrent ? 'step' : undefined}
                  onClick={() => {
                    if (isClickable) setStep(wizardStep.id);
                  }}
                  disabled={!isClickable && !isCurrent}
                >
                  {isCompleted ? (
                    <Check className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <span className="text-xs font-semibold">{wizardStep.id + 1}</span>
                  )}
                </button>

                <div className="mt-1.5 text-center">
                  <p
                    className={cn(
                      'text-xs font-medium whitespace-nowrap',
                      isCurrent ? 'text-primary' : 'text-muted-foreground',
                    )}
                  >
                    {wizardStep.title}
                  </p>
                </div>
              </div>

              {!isLast && (
                <div
                  className={cn(
                    'mt-4 mx-1 flex-1 h-0.5',
                    isPast ? 'bg-primary' : 'bg-muted',
                  )}
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
